import { ColumnsType } from 'antd/es/table';
import { Pagination } from 'antd';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { formatPhone } from 'utils/formatPhone';
import ModalConfirm from 'components/Modal/ModalConfirm';
import Text from 'components/Text';
import Box from 'components/Box';
import Modal from 'components/Modal';
import Table from 'components/Table';
import Button from 'components/Button';
import Spin from 'components/Spin';
import SwitchCalendarType from 'features/report/pages/ReportPage/components/SwitchCalendarType';
import AppointmentDatePicker from 'features/report/pages/ReportPage/components/DatePicker';
import userActions from 'features/user/services/actions';
import userSelectors from 'features/user/services/selectors';
import { useModalCallerIdHistory } from './useModalCallerIdHistory';
import { ICallerIdHistoryItem, ICallerIdHistoryRef } from './types';
import SmsSentIcon from '../icons/smsSent';
import CallInIcon from '../icons/callIn';
import BookedCalendarIcon from '../icons/bookedCalendar';
import NewCusIcon from '../icons/newCus';
import RegularCustomerIcon from '../icons/regularCustomer';
import VipCrownIcon from '../icons/vipCrown';

type Props = {};

const ModalCallerIdHistory = forwardRef<ICallerIdHistoryRef, Props>((props, ref) => {
  const {
    visible,
    setVisible,
    data,
    summary,
    loadingSummary,
    loadingHistory,
    pagination,
    handleChangePage,
    onSendSMS,
    onBook,
    modalConfirm,
    handleResetPagination,
    CALLER_ID_TYPE,
  } = useModalCallerIdHistory(ref);

  const summaryCardsData = React.useMemo(() => [
    { key: 'callIn', label: 'Call In', value: summary.callIn, icon: CallInIcon },
    { key: 'smsSent', label: 'SMS Sent', value: summary.smsSent, icon: SmsSentIcon },
    { key: 'booked', label: 'Booked', value: summary.booked, icon: BookedCalendarIcon },
    { key: 'newCus', label: 'New Cus', value: summary.newCus, icon: NewCusIcon },
    { key: 'regular', label: 'Regular', value: summary.regular, icon: RegularCustomerIcon },
    { key: 'vipCus', label: 'VIP Cus', value: summary.vipCus, icon: VipCrownIcon },
  ], [summary]);

  const columns: ColumnsType<ICallerIdHistoryItem> = [
    {
      title: 'Date & Time',
      dataIndex: 'dateTime',
      key: 'dateTime',
      align: 'center',
      render: (text) => moment(text).format('MM-DD-YYYY'),
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      align: 'center',
      render: (text, record) => {
        if (text) return text;
        if (record.dateTime) return moment(record.dateTime).format('hh:mm A');
        return '-';
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      align: 'left',
      render: (text) => text || '--',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      align: 'left',
      render: (text) => formatPhone(text),
    },
    {
      title: 'Customer Type',
      dataIndex: 'total',
      key: 'total',
      align: 'center',
      render: (text) => (
        <TotalTag className={text}>
          <Text variant="BODY_1" style={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}>{text}</Text>
        </TotalTag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 300,
      render: (_, record) => {
        const isBooked = record.booked === true;
        const isSmsSent = record.sentSms === true;
        const isDisabled = isBooked || isSmsSent;

        return (
          <ActionsBox>
            <ActionButton
              onClick={() => onSendSMS(record.phone, record.callerId)}
              disabled={isDisabled}
            >
              Send SMS
            </ActionButton>
            <ActionButton
              className="btn-primary"
              onClick={() => onBook(record)}
              disabled={isBooked}
            >
              Booked
            </ActionButton>
          </ActionsBox>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        modalTitle="HISTORY CALLER ID"
        visible={visible}
        onClose={() => setVisible(false)}
        footer={null}
        width={1500}
        destroyOnClose
        noneBodyStyle
        v2
      >
        <Container>
          <FixedTopSection>
            <FilterBox>
              <SwitchCalendarType
                v2
                twoWeeks
                actions={userActions}
                selectors={userSelectors}
                type={CALLER_ID_TYPE}
                onChangeValue={handleResetPagination}
              />
              <AppointmentDatePicker
                v2
                size="middle"
                actions={userActions}
                selectors={userSelectors}
                type={CALLER_ID_TYPE}
                onChangeValue={handleResetPagination}
              />
            </FilterBox>

            <SummaryCards>
              {summaryCardsData.map(({ key, label, value, icon: Icon }) => (
                <SummaryCard key={key}>
                  <SummaryCardContent>
                    <SummaryCardText>
                      <Text variant="BODY_1" color="text_3" style={{ fontSize: '18px', fontWeight: 600, lineHeight: '30px', textTransform: 'capitalize' }}>
                        {label}
                      </Text>
                      {loadingSummary ? (
                        <LoadingWrapper>
                          <Spin />
                        </LoadingWrapper>
                      ) : (
                        <Text variant="H2" color="text_2" style={{ fontSize: '32px', fontWeight: 600, lineHeight: 'normal' }}>
                          {value}
                        </Text>
                      )}
                    </SummaryCardText>
                    <SummaryCardIcon>
                      <Icon />
                    </SummaryCardIcon>
                  </SummaryCardContent>
                </SummaryCard>
              ))}
            </SummaryCards>
          </FixedTopSection>

          <ScrollableTableSection>
            <TableStyled>
              <Table
                columns={columns}
                dataSource={data}
                loading={loadingHistory}
                rowKey="id"
                pagination={undefined}
              />
            </TableStyled>
          </ScrollableTableSection>

          {pagination.total > 0 && !loadingHistory && (
            <PaginationStyled>
              <Pagination
                current={pagination.current}
                total={pagination.total}
                pageSize={pagination.pageSize}
                showSizeChanger={false}
                onChange={handleChangePage}
                showLessItems
              />
            </PaginationStyled>
          )}

          <FixedBottomSection>
            <Button ntype='SECONDARY' width='100%' onClick={() => setVisible(false)}>
              Close
            </Button>
          </FixedBottomSection>
        </Container>
      </Modal>
      <ModalConfirm ref={modalConfirm} />
    </>
  );
});

ModalCallerIdHistory.displayName = 'ModalCallerIdHistory';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 250px);
  max-height: 900px;
  padding: 16px;
  overflow: hidden;
`;

const FixedTopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex-shrink: 0;
  background: #fff;
  z-index: 10;
`;

const ScrollableTableSection = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 16px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const FixedBottomSection = styled.div`
  flex-shrink: 0;
  background: #fff;
  z-index: 10;
  padding-top: 16px;
`;

const FilterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const SummaryCards = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px;
  background: #F5F5F5;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.15);
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 40px;
`;

const SummaryCard = styled(Box)`
  flex: 1 0 0;
  background: #FFFFFF;
  border: 1px solid #CCD4DC;
  border-radius: 8px;
  padding: 24px 8px 24px 16px;
`;

const SummaryCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
`;

const SummaryCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 0 0;
`;

const SummaryCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
`;

const TableStyled = styled.div`
  flex-shrink: 0;
  .ant-table-wrapper {
    overflow: visible;
    .ant-table {
      .ant-table-container {
        .ant-table-content {
          overflow: visible;
          border: none;
          .ant-table-thead {
            overflow: hidden;
            position: sticky;
            top: 0;
            z-index: 10;
            
            th.ant-table-cell {
              position: sticky;
              top: 0;
              z-index: 99;
              background: #6fabb6 !important;
              border-bottom: 1px solid #ccd4dc;
              color: #fff !important;
              height: 52px;
              padding: 8px;
              font-family: Poppins;
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
              position: relative;
              &::before {
                content: none;
              }
              
              &:not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 8px;
                bottom: 8px;
                width: 2px;
                background: rgba(255, 255, 255, 0.3);
                z-index: 1;
              }
            }
            
            tr th:first-child {
              border-top-left-radius: 8px !important;
            }
            
            tr th:last-child {
              border-top-right-radius: 8px !important;
            }
          }
          .ant-table-tbody {
            .ant-table-row {
              background: #ffffff;
              &:nth-child(even) {
                background: #eef4ff;
              }
            }
          }
        }
      }
    }
    
    .ant-table .ant-table-thead th.ant-table-cell {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #6fabb6 !important;
      border-bottom: 1px solid #ccd4dc;
      color: #fff !important;
      height: 52px;
      padding: 8px;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      position: relative;
      &::before {
        content: none;
      }
      
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: rgba(255, 255, 255, 0.3);
      }
    }
    
    .ant-table .ant-table-thead tr th:first-child {
      border-top-left-radius: 8px !important;
    }
    
    .ant-table .ant-table-thead tr th:last-child {
      border-top-right-radius: 8px !important;
    }
    
    .ant-table-tbody td.ant-table-cell {
      height: auto;
      padding: 2px 8px;
      color: #131921 !important;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
      
      .BODY_1, .CONTENT_1, .CAPTION_1 {
        color: #131921 !important;
      }
    }
    
    .ant-table-tbody .ant-table-row:nth-child(even) td {
      background: #eef4ff;
    }
  }
`;

const PaginationStyled = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
  flex-shrink: 0;
  background: #fff;
  z-index: 10;

  .ant-pagination {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    
    .ant-pagination-item,
    .ant-pagination-next,
    .ant-pagination-prev {
      border-radius: 3.333px;
      background: #fff;
      border: 1px solid #86909c;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin: 0 4px;
      
      a {
        color: #1d2129;
        text-align: center;
        font-family: Poppins;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
      }

      &.ant-pagination-item-active {
        background: #6FABB6;
        border-color: #6FABB6;
        a {
          color: #fff;
        }
      }

      &:hover:not(.ant-pagination-item-active) {
        background: #E5E6EB;
        border-color: #E5E6EB;
      }
    }

    .ant-pagination-jump-next,
    .ant-pagination-jump-prev {
      .ant-pagination-item-container {
        .ant-pagination-item-link-icon {
          color: #1D2129;
        }
      }
    }

    .ant-pagination-disabled {
      opacity: 0.5;
      cursor: not-allowed;
      
      &:hover {
        background: #fff;
        border-color: #86909c;
      }
    }
  }
`;

const TotalTag = styled.div`
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 5px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  &.New {
    background: #FFF1F6;
    color: #D2464F;
  }
  &.Regular {
    background: #F2EEFC;
    color: #845ADF;
  }
  &.VIP {
    background: #FDF4CE;
    color: #EBA33B;
  }
`;

const ActionsBox = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ActionButton = styled.button`
  display: flex;
  flex: 1 0 0;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: 1px solid #2d6772;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.4), 0px 4px 8px -3px rgba(45, 35, 66, 0.3);
  
  color: #2d6772;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;

  &::after {
    content: '';
    position: absolute;
    inset: -0.5px;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: inset 0px -3px 0px 0px #144b55;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
    
    &::after {
      display: none;
    }
  }

  &.btn-primary {
    background: #0da472;
    border-color: #0da472;
    color: #fff;
    
    &::after {
      box-shadow: inset 0px -3px 0px 0px #0b8d5f;
    }
    
    &:hover:not(:disabled) {
      background: #0b8d5f;
      border-color: #0b8d5f;
    }
    
    &:disabled {
      background: #cccccc;
      border-color: #cccccc;
      color: #666666;
    }
  }
`;


export default ModalCallerIdHistory;
export type { ICallerIdHistoryRef, ICallerIdHistoryItem } from './types';
